"use client";

import {
  CreateTrialRequestMutation,
  CustomerSource,
  GetAvailableSlotsQuery,
  TrialRequestCreateInput,
  TrialRequestSource,
  TrialRequestStatus,
  useGetAvailableSlotsLazyQuery,
  useGetTrialRequestQuery,
} from "@/__generated__/graphql";
import { useCreateTrialRequest, useUpdateTrialRequest } from "@/app/form/data";
import { ScheduleFormData } from "@/app/form/schema";
import { Button } from "@/components/ui/button";
import { Form, FormLabel, FormRootError } from "@/components/ui/form";
import { FormInput } from "@/components/ui/form/input";
import { FormTextArea } from "@/components/ui/form/textarea";
import {
  Calendar,
  CalendarCurrentDate,
  CalendarMonthView,
  CalendarNextTrigger,
  CalendarPrevTrigger,
} from "@/components/ui/full-calendar";
import { CalendarIcon, ClockIcon } from "@/components/ui/icons";
import { WidgetAPI } from "@/context/widget-context";
import { Language, useTranslations } from "@/lib";
import { cn } from "@/lib/utils";
import { ApolloError } from "@apollo/client";
import { addDays, differenceInMinutes, format, startOfDay } from "date-fns";
import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormSelect } from "../ui/form/select";
import Spinner from "../ui/spinner";
const REGION_OPTIONS = [
  {
    value: "Dubai",
    label: {
      en: "Dubai",
      ar: "دبي",
    },
  },
  {
    value: "Abu Dhabi",
    label: {
      en: "Abu Dhabi",
      ar: "أبو ظبي",
    },
  },
  {
    value: "Sharjah",
    label: {
      en: "Sharjah",
      ar: "الشارقة",
    },
  },
  {
    value: "Ajman",
    label: {
      en: "Ajman",
      ar: "عجمان",
    },
  },
  {
    value: "Ras Al Khaimah",
    label: {
      en: "Ras Al Khaimah",
      ar: "رأس الخيمة",
    },
  },
  {
    value: "Fujairah",
    label: {
      en: "Fujairah",
      ar: "الفجيرة",
    },
  },
  {
    value: "Umm Al Quwain",
    label: {
      en: "Umm Al Quwain",
      ar: "أم القيوين",
    },
  },
];

// Map regions to slugs
const REGION_SLUG_MAP: Record<string, string> = {
  Dubai: "dubai-kh",
  "Abu Dhabi": "abuDhabi-kh",
  Sharjah: "abuDhabi-kh",
  // All other regions will default to "others-kh"
};
export function Schedule({
  lang,
  form,
  widget,
  slots,
  setSlots,
}: {
  lang: Language;
  form: UseFormReturn<ScheduleFormData>;
  widget: WidgetAPI;
  slots: GetAvailableSlotsQuery | undefined;
  setSlots: (slots: GetAvailableSlotsQuery) => void;
}) {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslations(lang);
  const createTrialRequest = useCreateTrialRequest({
    onCompleted: (data: CreateTrialRequestMutation) => {
      widget.setStep(6);
      widget.setTrialRequestId(data.createTrialRequest.id);
    },
  });
  const { loading: fetchLoading } = useGetTrialRequestQuery({
    variables: {
      id: widget.trialRequestId,
    },
    skip: !widget.trialRequestId,
    onCompleted: (data) => {
      form.reset({
        emirate: form.getValues("emirate"),
        region: data.trialRequest?.region || "",
        date: new Date(data.trialRequest?.startDateTime),
        startDateTime: data.trialRequest?.startDateTime,
        duration: data.trialRequest?.duration || undefined,
        name: data.trialRequest?.customer.name,
        phone: data.trialRequest?.customer.phone.replace("+971", ""),
        email: data.trialRequest?.customer.email || "",
        address: data.trialRequest?.customer?.address || "",
      });
      handleRegionChange(form.getValues("emirate"));
    },
  });
  const updateTrialRequestMutation = useUpdateTrialRequest({
    onCompleted: (data) => {
      widget.setStep(6);
      widget.setTrialRequestId(data.updateTrialRequest.id);
    },
  });
  async function onSubmit(values: ScheduleFormData) {
    setLoading(true);

    try {
      // Validate phone (assumes throws if invalid)
      validatePhoneNumber(values.phone);
      const trialRequestId = widget.trialRequestId;
      const parsedPhone = parsePhoneNumberFromString(values.phone, "AE");
      const variables: TrialRequestCreateInput = {
        customer: {
          name: values.name,
          email: values.email,
          phone: parsedPhone?.number ?? "",
          address: values.address,
          source: CustomerSource.KnotHome,
        },
        products: widget.products?.map((product) => String(product.id)),
        region: values.region,
        startDateTime: values.startDateTime,
        duration: values.duration,
        source: TrialRequestSource.KnotHome,
        status: TrialRequestStatus.Pending,
        utm: window.location.href,
      };

      if (trialRequestId) {
        await updateTrialRequestMutation(String(trialRequestId), variables);
      } else {
        await createTrialRequest(variables);
      }
    } catch (error) {
      let message = t("schedule.form.error.went_wrong");
      if (error instanceof ApolloError) {
        message =
          error.graphQLErrors[0]?.message ||
          error.networkError?.message ||
          message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      form.setError("root", {
        type: "manual",
        message,
      });
    } finally {
      setLoading(false);
    }
  }

  const [fetchSlots, { loading: isLoading }] = useGetAvailableSlotsLazyQuery({
    onError: (error) => {
      console.error(error);
    },
    onCompleted: (data) => {
      setSlots(data);
    },
  });
  async function handleRegionChange(value: string) {
    form.setValue("region", REGION_SLUG_MAP[value] ?? "others-kh");
    form.setValue("emirate", value);
    await fetchSlots({
      variables: {
        startDate: addDays(new Date(), 1),
        scheduleSlug: form.watch("region"),
        endDate: addDays(addDays(new Date(), 1), 14),
      },
    });
    setTimeout(() => {
      document.getElementById("calendar")?.scrollIntoView({
        behavior: "smooth",
      });
    }, 100);
  }
  function validatePhoneNumber(value: string) {
    const phone = parsePhoneNumberFromString(value, "AE");
    if (!value || !phone) {
      form.setError("phone", {
        type: "manual",
        message: t("schedule.form.error.valid_number"),
      });
      return;
    }

    // Use isPossiblePhoneNumber for "as-you-type" validation (checks length/pattern)
    if (!isPossiblePhoneNumber(value, "AE")) {
      form.setError("phone", {
        type: "manual",
        message: t("schedule.form.error.incomplete"),
      });
    } else if (!isValidPhoneNumber(value, "AE")) {
      form.setError("phone", {
        type: "manual",
        message: t("schedule.form.error.invalid"),
      });
    } else {
      form.clearErrors("phone");
    }
  }

  return fetchLoading ? (
    <Spinner className="tw:w-10 tw:h-10 tw:mx-auto" />
  ) : (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="tw:px-4 tw:pb-10">
            <span className="tw:font-semibold">
              {t("schedule.emirate_text")}
            </span>

            <FormSelect
              className="tw:mt-2"
              form={form}
              name="emirate"
              options={REGION_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label[lang],
              }))}
              onChange={(value) => {
                handleRegionChange(value);
              }}
              placeholder={t("schedule.form.placeholder.select")}
              required
            />
          </div>

          {isLoading ? (
            <Spinner className="tw:mx-auto tw:mt-10" />
          ) : slots ? (
            <>
              <div className="tw:px-4 tw:py-10 tw:!border-t" id="calendar">
                <Calendar>
                  <div className="tw:flex tw:flex-col tw:w-full">
                    <div className="tw:items-center tw:justify-between tw:gap-2 tw:mb-3 tw:sm:flex">
                      <span className="tw:font-semibold">
                        <CalendarIcon className="tw:!inline tw:mr-2" />
                        {t("schedule.available_date_text")}
                      </span>
                      <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
                        <CalendarPrevTrigger>
                          <ChevronLeft size={20} />
                          <span className="tw:sr-only">
                            {t("schedule.previous_text")}
                          </span>
                        </CalendarPrevTrigger>

                        <CalendarCurrentDate />

                        <CalendarNextTrigger>
                          <ChevronRight size={20} />
                          <span className="tw:sr-only">
                            {t("schedule.next_text")}
                          </span>
                        </CalendarNextTrigger>
                      </div>
                    </div>

                    <div className="tw:flex-1 tw:overflow-hidden">
                      <CalendarMonthView
                        onDateSelect={(date) => {
                          form.setValue("date", date, {
                            shouldValidate: true,
                          });
                          setTimeout(() => {
                            document.getElementById("slots")?.scrollIntoView({
                              behavior: "smooth",
                            });
                          }, 100);
                        }}
                        availableDates={
                          slots.availableSlotsForSchedule.map(
                            (slot) => slot.date,
                          ) || []
                        }
                        selectedDate={form.watch("date")}
                      />
                    </div>
                  </div>
                </Calendar>
                <FormInput
                  form={form}
                  type="hidden"
                  name="date"
                  className="tw:mt-2"
                />
              </div>

              <div
                id="slots"
                className={cn(
                  "tw:px-4 tw:py-10",
                  form.watch("date") ? "tw:!border-t" : "tw:mb-44",
                )}
              >
                {form.watch("date") ? (
                  <>
                    <div className="tw:">
                      <span className="tw:font-semibold">
                        <ClockIcon className="tw:!inline tw:mr-2" />
                        {t("schedule.select_time_text")}
                      </span>

                      <div
                        className={cn(
                          "tw:flex tw:flex-wrap tw:items-center tw:gap-5 tw:mt-2 tw:ml-auto",
                          lang === "ar" && "tw:flex-row-reverse tw:w-full",
                        )}
                      >
                        {slots.availableSlotsForSchedule.map((slot, index) => {
                          const selectedDate = startOfDay(
                            form.watch("date"),
                          )?.toISOString();
                          const slotDate = startOfDay(
                            new Date(slot.date),
                          )?.toISOString();
                          if (selectedDate === slotDate && slot.slots) {
                            return slot.slots.map((time, timeIndex) => (
                              <Button
                                variant="outline"
                                type="button"
                                className={cn(
                                  "tw:!px-4 tw:!py-2 tw:!w-3/4 tw:md:!w-1/3",
                                  form.watch("startDateTime") ===
                                    String(time.startTime)
                                    ? "tw:!bg-primary tw:hover:!bg-primary"
                                    : "tw:!text-gray-700",
                                )}
                                key={`${index}-${timeIndex}`}
                                onClick={() => {
                                  form.setValue(
                                    "startDateTime",
                                    String(time.startTime),
                                  );
                                  form.setValue(
                                    "duration",
                                    differenceInMinutes(
                                      String(time.endTime),
                                      String(time.startTime),
                                    ),
                                  );
                                  setTimeout(() => {
                                    document
                                      .getElementById("contact")
                                      ?.scrollIntoView({
                                        behavior: "smooth",
                                      });
                                  }, 100);
                                }}
                              >
                                {time.startTime
                                  ? format(new Date(time.startTime), "hh:mm a")
                                  : ""}{" "}
                                -{" "}
                                {time.endTime
                                  ? format(new Date(time.endTime), "hh:mm a")
                                  : ""}
                              </Button>
                            ));
                          }
                          return null;
                        })}
                      </div>
                    </div>
                    <FormInput
                      form={form}
                      type="hidden"
                      name="duration"
                      className="tw:mt-2"
                    />
                  </>
                ) : null}
              </div>
            </>
          ) : null}

          {form.watch("date") && form.watch("startDateTime") ? (
            <div
              className="tw:px-4 tw:py-10 tw:mb-44 tw:md:mb-20 tw:!border-t"
              id="contact"
            >
              <span className="tw:block tw:mb-5 tw:font-semibold">
                {t("schedule.contact_details_text")}
              </span>

              <div
                className={cn("tw:grid tw:grid-cols-1 tw:gap-4")}
                dir={lang === "ar" ? "rtl" : ""}
              >
                <FormInput
                  form={form}
                  name="name"
                  label={t("schedule.form.label.name")}
                  placeholder={t("schedule.form.placeholder.name")}
                  required
                />

                <FormInput
                  form={form}
                  type="email"
                  name="email"
                  label={t("schedule.form.label.email")}
                  placeholder={t("schedule.form.placeholder.email")}
                  required
                />

                <div className="tw:flex tw:flex-wrap tw:items-end tw:justify-between">
                  <FormLabel className="tw:w-full tw:mb-2 tw:text-sm tw:font-semibold tw:gap-0">
                    {t("schedule.phone_number_text")}
                    <span className="tw:text-destructive">*</span>
                  </FormLabel>
                  <div className="tw:md:w-[28%] tw:mb-auto tw:w-[35%]">
                    <div className="tw:!border tw:!border-[#E9E3DA] tw:h-12 tw:px-3 tw:py-3.5 tw:text-[#909090] tw:font-medium tw:text-sm">
                      {t("schedule.country_code_text")}
                    </div>
                  </div>

                  <FormInput
                    form={form}
                    type="text"
                    name="phone"
                    placeholder={t("schedule.form.placeholder.phone")}
                    required
                    className="tw:md:w-[70%] tw:w-[60%]"
                    onChange={(e) => {
                      form.setValue("phone", e.target.value || "");
                      validatePhoneNumber(e.target.value);
                    }}
                  />
                </div>

                <FormTextArea
                  form={form}
                  name="address"
                  label={t("schedule.form.label.address")}
                  placeholder={t("schedule.form.placeholder.address")}
                  rows={3}
                  required
                />
              </div>
            </div>
          ) : (
            <div className="tw:mt-44 tw:md:mt-20">&nbsp;</div>
          )}

          <div className="tw:fixed tw:bottom-0 tw:bg-white tw:w-[95%] tw:md:w-lg hide-footer--btn">
            <div className="tw:text-center tw:!border-t">
              <FormRootError className="tw:mb-2 tw:text-center" />
              <Button
                type="submit"
                className="tw:!px-20 tw:md:!px-32 tw:!text-sm tw:md:!text-base tw:w-full tw:!h-13.5 tw:disabled:opacity-80 tw:disabled:!cursor-not-allowed"
                disabled={
                  loading ||
                  !(form.watch("date") && form.watch("startDateTime")) ||
                  Object.keys(form.formState.errors).length > 0
                }
              >
                {t("confirm_and_continue_text")}
                {loading && <Spinner className="tw:h-5" />}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  );
}
